// ---------------------------------------------------------------------------
// 部門集計表 データ取得・整形 (2026-11-05)
//   旧「部門集計表」(勘定奉行 PDF 参考) を freee からライブ生成する。
//   - データ源: freee trial_pl を breakdown_display_type=section で 1 回取得
//     (全部門 × 全勘定科目 の発生額 = closing - opening を 1 レスポンスで得る。部門ごとの 36 回呼出を回避)。
//   - 行構造: 部門別予算実績ページの 69 行レイアウト(budgetActualLayout)を踏襲し、
//     freee 実績が引けない予算専用行(本部売上/退職金/本部経費/店舗経常利益 等)は省く。
//     テンプレ外の freee 勘定科目は所属カテゴリーの小計直前へ自動挿入(黙殺禁止)。
//   - 家賃除外: 地代家賃 / 市場家賃 / 市場家賃振替 を選択式で除外。除外した科目は行を出さず、
//     販売経費以下の全小計からも差し引く(= データから取り除いてから集計)。表頭に注記。
//   - 合計列 = 全社(freee 勘定科目レベルの会社合計。部門未設定分も内包)。
//   - 小計は全列を同一式で明細から算出(部門別は freee のグループ集計が無いため)。
//     合計列については freee の total_line と突合し、不一致は meta.checks に載せる。
//   - 金額は trial_pl と同じ税抜(2026-11-05 実測で確認。deals の amount は税込・amount-vat が trial_pl と一致)。税込化は未実装。
// ---------------------------------------------------------------------------

import { adjustmentQuery } from './adjustmentMode';
import {
  BUDGET_LAYOUT, LABOR_LABELS, COGS_TERMS, CATEGORY_TO_SUBTOTAL,
} from '../data/budgetActualLayout';

const API_BASE = 'https://invoice-review.system-0f7.workers.dev';
const COMPANY_ID = 11518223;

export interface Range { start: string; end: string; }

/** 家賃系 3 科目(freee 勘定科目名)。既定は全て除外。 */
export const RENT_ACCOUNTS = ['地代家賃', '市場家賃', '市場家賃振替'] as const;
export type RentAccount = typeof RENT_ACCOUNTS[number];

export interface DeptColumn {
  key: string;
  label: string;
  /** total=全社(勘定科目レベル合計) / section=単一部門 / combined=仮想合算(山城屋) / group=グループ小計 */
  kind: 'total' | 'section' | 'combined' | 'group';
  sectionIds: number[];
}

export interface DeptRow {
  label: string;
  kind: 'detail' | 'subtotal' | 'blank';
  indent: number;
  /** テンプレ外の freee 勘定科目を自動挿入した行 */
  isNew?: boolean;
  /** 上段(〜一般管理費) / 下段(営業利益〜) の印刷分割 */
  part: 'upper' | 'lower';
  /** 列 key -> 金額 */
  values: Record<string, number>;
  /** 全列 0 (非表示トグル用) */
  allZero: boolean;
}

export interface DeptCheck { label: string; computed: number; freee: number; }

export interface DeptSummaryResult {
  columns: DeptColumn[];
  rows: DeptRow[];
  range: Range;
  printedAt: string;
  excludedRent: RentAccount[];
  meta: {
    empty: boolean;
    /** テンプレ外で自動挿入した科目名 */
    newAccounts: string[];
    /** 合計列の小計と freee total_line の突合(不一致のみ) */
    checks: DeptCheck[];
    /** 部門未設定(全社 − 全部門合算)が 0 でない科目 */
    unassigned: { label: string; amount: number }[];
  };
}

interface AccountData {
  name: string;
  category: string;
  total: number;                 // 全社 発生額
  bySection: Map<number, number>; // section_id -> 発生額
}

interface TrialSections {
  accounts: Map<string, AccountData>;
  groupTotals: Map<string, number>; // total_line account_category_name -> 発生額
}

/** freee trial_pl は「年度を跨ぐ日付指定」を 400 で拒否するため、期間を会計年度(10/1 起点)ごとに分割する。 */
function splitByFiscalYear(range: Range): Range[] {
  const out: Range[] = [];
  let start = range.start;
  while (start <= range.end) {
    const y = Number(start.slice(0, 4)); const m = Number(start.slice(5, 7));
    const fyEndYear = m >= 10 ? y + 1 : y;              // この会計年度の末日 = 翌年(or当年) 9/30
    const fyEnd = `${fyEndYear}-09-30`;
    const end = fyEnd < range.end ? fyEnd : range.end;
    out.push({ start, end });
    start = `${fyEndYear}-10-01`;
  }
  return out;
}

function mergeTrialSections(parts: TrialSections[]): TrialSections {
  if (parts.length === 1) return parts[0];
  const accounts = new Map<string, AccountData>();
  const groupTotals = new Map<string, number>();
  for (const p of parts) {
    p.groupTotals.forEach((v, k) => groupTotals.set(k, (groupTotals.get(k) || 0) + v));
    p.accounts.forEach((a, nm) => {
      const d = accounts.get(nm) || { name: nm, category: a.category, total: 0, bySection: new Map<number, number>() };
      d.total += a.total;
      a.bySection.forEach((v, sid) => d.bySection.set(sid, (d.bySection.get(sid) || 0) + v));
      accounts.set(nm, d);
    });
  }
  return { accounts, groupTotals };
}

async function fetchTrialSections(range: Range): Promise<TrialSections> {
  const parts: TrialSections[] = [];
  for (const r of splitByFiscalYear(range)) parts.push(await fetchTrialSectionsOne(r)); // 直列(429 回避)
  return mergeTrialSections(parts);
}

async function fetchTrialSectionsOne(range: Range): Promise<TrialSections> {
  const q = new URLSearchParams({
    company_id: String(COMPANY_ID),
    start_date: range.start, end_date: range.end,
    account_item_display_type: 'account_item',
    breakdown_display_type: 'section',
    ...adjustmentQuery(), // 整理仕訳 含む/除く (決算書ページと共通トグル)
  });
  const url = `${API_BASE}/api/freee-proxy/api/1/reports/trial_pl?${q.toString()}`;
  let res: Response | undefined;
  for (let attempt = 0; attempt < 4; attempt++) {
    res = await fetch(url);
    if (res.ok) break;
    if ((res.status === 429 || res.status === 503) && attempt < 3) {
      await new Promise((r) => setTimeout(r, 700 * (attempt + 1)));
      continue;
    }
    break;
  }
  if (!res || !res.ok) {
    let body: any = null; try { body = res ? await res.json() : null; } catch { /* */ }
    const msgs: string[] = [];
    if (body?.message) msgs.push(String(body.message));
    for (const e of (body?.errors || [])) for (const m of (e?.messages || [])) msgs.push(String(m));
    throw new Error(`freee 試算表の取得に失敗しました (${res ? res.status : 'no-response'}) ${msgs.join(' / ')}`);
  }
  const j = await res.json();
  const balances: any[] = (j?.trial_pl?.balances) || [];
  const accounts = new Map<string, AccountData>();
  const groupTotals = new Map<string, number>();
  for (const b of balances) {
    const hatsu = (b.closing_balance ?? 0) - (b.opening_balance ?? 0);
    if (b.total_line) {
      const cat = b.account_category_name;
      if (cat && !groupTotals.has(cat)) groupTotals.set(cat, hatsu);
      continue;
    }
    const nm = b.account_item_name as string | undefined;
    if (!nm) continue;
    const a = accounts.get(nm) || { name: nm, category: b.account_category_name || '', total: 0, bySection: new Map<number, number>() };
    a.total += hatsu;
    for (const s of (b.sections || [])) {
      const sid = Number(s.id);
      const sh = (s.closing_balance ?? 0) - (s.opening_balance ?? 0);
      a.bySection.set(sid, (a.bySection.get(sid) || 0) + sh);
    }
    accounts.set(nm, a);
  }
  return { accounts, groupTotals };
}

// --- 行テンプレ(予算実績ページ 69 行から freee 実績行のみ) ---------------------
interface TplRow {
  label: string;
  kind: 'detail' | 'subtotal';
  freeeName?: string;
  /** subtotal の種別(予算実績ページの subtotalKind) */
  subtotalKind?: string;
  indent: number;
}

/** 予算専用(freee 実績なし)で本表では省く行 */
const OMIT_LABELS = new Set(['本部経費', '店舗経常利益', '(社員割引)']);

function buildTemplate(): TplRow[] {
  const out: TplRow[] = [];
  for (const r of BUDGET_LAYOUT) {
    if (OMIT_LABELS.has(r.label)) continue;
    if (r.kind === 'subtotal') { out.push({ label: r.label, kind: 'subtotal', subtotalKind: r.subtotalKind, indent: 0 }); continue; }
    if (r.label === '家賃') {
      // 本表の家賃は freee 実科目(地代家賃)。市場家賃/市場家賃振替 も明示行にして除外対象を透明にする。
      out.push({ label: '家賃', kind: 'detail', freeeName: '地代家賃', indent: 1 });
      out.push({ label: '市場家賃', kind: 'detail', freeeName: '市場家賃', indent: 1 });
      out.push({ label: '市場家賃振替', kind: 'detail', freeeName: '市場家賃振替', indent: 1 });
      continue;
    }
    if (r.freeeCategory) {
      // 特別利益/特別損失: leaf 無し科目。本表ではカテゴリー配下 leaf の合算小計として扱う。
      out.push({ label: r.label, kind: 'subtotal', subtotalKind: r.label === '特別利益' ? 'extra_income' : 'extra_loss', indent: 0 });
      continue;
    }
    if (!r.freeeName) continue; // 予算専用行は省く
    out.push({ label: r.label, kind: 'detail', freeeName: r.freeeName, indent: 1 });
    // 社員割引(売上高カテゴリーの控除科目)は 売上 の直後に明示行(net)で置く
    if (r.label === '売上') out.push({ label: '社員割引', kind: 'detail', freeeName: '社員割引', indent: 1 });
  }
  // 税引前当期利益 の後に 法人税等 / 当期純利益 を追加(参考 PDF 踏襲)
  out.push({ label: '法人税等', kind: 'subtotal', subtotalKind: 'tax', indent: 0 });
  out.push({ label: '当期純利益', kind: 'subtotal', subtotalKind: 'net_income', indent: 0 });
  return out;
}

/** subtotal ラベル → その直前に自動挿入する(container)。CATEGORY_TO_SUBTOTAL の対応先を本表の行ラベルへ寄せる。 */
const CONTAINER_OF_TARGET: Record<string, string> = {
  '純売上高': '純売上高', '売上原価': '売上原価', '販売経費': '販売経費',
  '営業外収益': '営業外収益', '営業外費用': '営業外費用', '特別利益': '特別利益', '特別損失': '特別損失',
};

function containerForCategory(cat: string): string {
  if (cat.includes('法人税')) return '法人税等';
  const hit = CATEGORY_TO_SUBTOTAL.find((c) => cat.includes(c.match));
  return hit ? (CONTAINER_OF_TARGET[hit.subtotal] || '販売経費') : '販売経費';
}

/** 売上原価 明細の符号(期末棚卸=控除) */
function cogsSign(label: string, category: string): 1 | -1 {
  const t = COGS_TERMS.find((x) => x.label === label);
  if (t) return t.sign;
  return category.includes('期末商品棚卸') ? -1 : 1;
}

const SALES_SUBTOTAL = '純売上高';
const SELLING_SUBTOTAL = '販売経費';

/** 明細行がどの container(小計ラベル) に属するか = テンプレ順で次に現れる subtotal */
function containerOfTemplateRow(tpl: TplRow[], idx: number): string {
  for (let i = idx + 1; i < tpl.length; i++) {
    if (tpl[i].kind === 'subtotal') {
      const l = tpl[i].label;
      // 人件費 明細 → 人件費 / 売上総利益・一般管理費 等の派生小計は container ではない
      return l;
    }
  }
  return '';
}

function ymdLabel(d: string): string {
  const [y, m, dd] = d.split('-').map(Number);
  return `${y}年${m}月${dd}日`;
}

function printedNow(): string {
  const n = new Date();
  const p = (x: number) => String(x).padStart(2, '0');
  return `${n.getFullYear()}年${p(n.getMonth() + 1)}月${p(n.getDate())}日`;
}

/**
 * 部門集計表を取得・組成。
 * @param range 集計期間(暦日)
 * @param columns 出力列(先頭に全社合計を含めるかは呼出側で決める)
 * @param excludedRent 除外する家賃科目
 */
export async function fetchDeptSummary(range: Range, columns: DeptColumn[], excludedRent: RentAccount[]): Promise<DeptSummaryResult> {
  const data = await fetchTrialSections(range);
  const excluded = new Set<string>(excludedRent);

  // 除外科目はデータから取り除く(行非表示 + 小計から差引 を同時に満たす)
  const excludedTotals: Record<string, number> = {};
  for (const nm of Array.from(excluded)) {
    const a = data.accounts.get(nm);
    if (a) { excludedTotals[nm] = a.total; data.accounts.delete(nm); }
  }

  const valueOf = (a: AccountData | undefined, col: DeptColumn): number => {
    if (!a) return 0;
    if (col.kind === 'total') return a.total;
    let s = 0;
    for (const sid of col.sectionIds) s += a.bySection.get(sid) || 0;
    return s;
  };
  const zeroVals = (): Record<string, number> => { const o: Record<string, number> = {}; for (const c of columns) o[c.key] = 0; return o; };
  const addInto = (dst: Record<string, number>, src: Record<string, number>, sign = 1) => { for (const c of columns) dst[c.key] += sign * (src[c.key] || 0); };
  const isAllZero = (v: Record<string, number>) => columns.every((c) => Math.round(v[c.key] || 0) === 0);

  const tpl = buildTemplate();
  const consumed = new Set<string>();
  for (const r of tpl) if (r.freeeName) consumed.add(r.freeeName);

  // テンプレ外 freee 勘定科目 → container 別に束ねる
  const newByContainer = new Map<string, AccountData[]>();
  const newAccounts: string[] = [];
  data.accounts.forEach((a, nm) => {
    if (consumed.has(nm)) return;
    if (a.total === 0 && Array.from(a.bySection.values()).every((v) => v === 0)) return;
    const target = containerForCategory(a.category);
    if (!newByContainer.has(target)) newByContainer.set(target, []);
    newByContainer.get(target)!.push(a);
    newAccounts.push(nm);
  });

  // 1) 明細行を組成(container 別の集計も同時に)
  type Built = { row: DeptRow; container: string; sign: 1 | -1 };
  const built: Built[] = [];
  const containerSum = new Map<string, Record<string, number>>();
  const bump = (container: string, v: Record<string, number>, sign: 1 | -1) => {
    if (!containerSum.has(container)) containerSum.set(container, zeroVals());
    addInto(containerSum.get(container)!, v, sign);
  };
  const mkDetail = (label: string, a: AccountData | undefined, indent: number, isNew: boolean): DeptRow => {
    const values = zeroVals();
    for (const c of columns) values[c.key] = valueOf(a, c);
    return { label, kind: 'detail', indent, isNew: isNew || undefined, part: 'upper', values, allZero: isAllZero(values) };
  };

  let part: 'upper' | 'lower' = 'upper';
  const rows: DeptRow[] = [];
  const subtotalRows: { row: DeptRow; kind: string }[] = [];

  for (let i = 0; i < tpl.length; i++) {
    const t = tpl[i];
    if (t.kind === 'detail') {
      const container = containerOfTemplateRow(tpl, i);
      const a = data.accounts.get(t.freeeName!);
      const row = mkDetail(t.label, a, t.indent, false);
      row.part = part;
      const sign = container === '売上原価' ? cogsSign(t.label, a?.category || '') : 1;
      built.push({ row, container, sign });
      bump(container, row.values, sign);
      rows.push(row);
      continue;
    }
    // subtotal 直前: その container へ寄せた新規科目を挿入
    const extra = newByContainer.get(t.label);
    if (extra) {
      for (const a of extra) {
        const row = mkDetail(a.name, a, 1, true);
        row.part = part;
        const sign = t.label === '売上原価' ? cogsSign(a.name, a.category) : 1;
        built.push({ row, container: t.label, sign });
        bump(t.label, row.values, sign);
        rows.push(row);
      }
    }
    const srow: DeptRow = { label: t.label, kind: 'subtotal', indent: 0, part, values: zeroVals(), allZero: false };
    rows.push(srow);
    subtotalRows.push({ row: srow, kind: t.subtotalKind || '' });
    if (t.label === '一般管理費') part = 'lower'; // 以降(営業利益〜)は下段
  }

  // 2) 小計の確定(全列同一式)
  const get = (label: string) => containerSum.get(label) || zeroVals();
  const sub = (kind: string) => subtotalRows.find((s) => s.kind === kind)?.row.values || zeroVals();
  const assign = (kind: string, v: Record<string, number>) => { const s = subtotalRows.find((x) => x.kind === kind); if (s) s.row.values = v; };
  const minus = (a: Record<string, number>, b: Record<string, number>) => { const o = zeroVals(); addInto(o, a); addInto(o, b, -1); return o; };
  const plus = (a: Record<string, number>, b: Record<string, number>) => { const o = zeroVals(); addInto(o, a); addInto(o, b); return o; };

  assign('net_sales', get(SALES_SUBTOTAL));
  assign('cogs', get('売上原価'));
  assign('gross_profit', minus(sub('net_sales'), sub('cogs')));
  assign('labor', get('人件費'));
  assign('selling', get(SELLING_SUBTOTAL));
  assign('sga', plus(sub('labor'), sub('selling')));
  assign('operating', minus(sub('gross_profit'), sub('sga')));
  assign('nonop_income', get('営業外収益'));
  assign('nonop_expense', get('営業外費用'));
  assign('ordinary', minus(plus(sub('operating'), sub('nonop_income')), sub('nonop_expense')));
  assign('extra_income', get('特別利益'));
  assign('extra_loss', get('特別損失'));
  assign('pretax', minus(plus(sub('ordinary'), sub('extra_income')), sub('extra_loss')));
  assign('tax', get('法人税等'));
  assign('net_income', minus(sub('pretax'), sub('tax')));
  for (const s of subtotalRows) s.row.allZero = isAllZero(s.row.values);

  // 3) 合計列の突合(freee total_line)。家賃除外分は販管費以下の期待値から差し引いて比較。
  const checks: DeptCheck[] = [];
  const totalCol = columns.find((c) => c.kind === 'total');
  if (totalCol) {
    const rentEx = Object.values(excludedTotals).reduce((a, b) => a + b, 0);
    const cmp = (kind: string, freeeCat: string, adj = 0) => {
      const f = data.groupTotals.get(freeeCat);
      if (f == null) return;
      const computed = Math.round(sub(kind)[totalCol.key] || 0);
      const expected = Math.round(f + adj);
      if (computed !== expected) checks.push({ label: freeeCat, computed, freee: expected });
    };
    cmp('net_sales', '売上高');
    cmp('cogs', '商品売上原価');
    cmp('sga', '販売管理費', -rentEx);
    cmp('nonop_income', '営業外収益');
    cmp('nonop_expense', '営業外費用');
    cmp('ordinary', '経常損益金額', +rentEx);
    cmp('net_income', '当期純損益金額', +rentEx);
  }

  // 4) 部門未設定の検出(freee は sections[] に id=0 として部門未設定分を返す。合計列にのみ含まれる)
  const unassigned: { label: string; amount: number }[] = [];
  data.accounts.forEach((a, nm) => {
    const d = Math.round(a.bySection.get(0) || 0);
    if (d !== 0) unassigned.push({ label: nm, amount: d });
  });

  // 5) 群間の空行(上段: 純売上高/売上総利益/人件費 の後、下段: 営業利益/営業外収益/営業外費用/経常利益/特別利益/特別損失/税引前/法人税等 の後)
  const BLANK_AFTER = new Set(['純売上高', '売上総利益', '人件費', '一般管理費', '営業利益', '営業外収益', '営業外費用', '経常利益', '特別利益', '特別損失', '税引前当期利益', '法人税等']);
  const withBlanks: DeptRow[] = [];
  for (const r of rows) {
    withBlanks.push(r);
    if (r.kind === 'subtotal' && BLANK_AFTER.has(r.label)) {
      withBlanks.push({ label: '', kind: 'blank', indent: 0, part: r.label === '一般管理費' ? 'lower' : r.part, values: {}, allZero: false });
    }
  }
  // 下段先頭の空行は不要
  const finalRows = withBlanks.filter((r, i) => !(r.kind === 'blank' && r.part === 'lower' && (i === 0 || withBlanks[i - 1].part === 'upper')));

  const empty = data.accounts.size === 0;
  return {
    columns, rows: finalRows, range, printedAt: printedNow(), excludedRent,
    meta: { empty, newAccounts, checks, unassigned },
  };
}

export const periodLabelOf = (r: Range) => `自 ${ymdLabel(r.start)}　至 ${ymdLabel(r.end)}`;
